import { useSafeApiOrgSlug } from '../../../utils/orgSlug'
import { useApiQuery } from '../../../utils/reactQuery'
import type { components } from '@joyful/openapi/types.gen'
import { keepPreviousData } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { isUuid } from '@joyful/utils'

const ENCOUNTER_SEARCH_DEBOUNCE_MS = 300

interface UseEncounterSearchParams {
  /**
   * @default 5
   */
  limit?: number

  /**
   * if not provided, will use active org slug
   */
  orgSlug?: string

  query: string
}

/**
 * ability to search for encounters matching text in current org or directly by uuid
 */
export function useEncounterSearch({
  limit = 5,
  orgSlug,
  query,
}: UseEncounterSearchParams) {
  const debouncedQuery = useDebounce(query, ENCOUNTER_SEARCH_DEBOUNCE_MS)
  const activeOrgSlug = useSafeApiOrgSlug()
  const isSearchValueUuid = isUuid(debouncedQuery)
  const finalOrgSlug = orgSlug ?? activeOrgSlug
  const hasOrgSlug = finalOrgSlug !== null

  const encountersQuery = useApiQuery(
    'get',
    '/{org_slug}/encounters',
    {
      params: {
        path: {
          org_slug: finalOrgSlug ?? '',
        },
        query: {
          limit,
          page: 1,
          query: debouncedQuery,
          sort_key: 'service_date',
          sort_order: 'desc',
        },
      },
    },
    {
      enabled: hasOrgSlug && isSearchValueUuid === false,
      placeholderData: keepPreviousData,
    },
  )

  const encounterQuery = useApiQuery(
    'get',
    '/{org_slug}/encounters/{encounter_id}',
    {
      params: {
        path: {
          encounter_id: debouncedQuery,
          org_slug: finalOrgSlug ?? '',
        },
      },
    },
    {
      enabled: hasOrgSlug && isSearchValueUuid === true,
    },
  )

  let encounters: Array<
    Pick<
      components['schemas']['EncounterListItem'],
      | 'account'
      | 'enrichment_status'
      | 'enrichment_status_override'
      | 'enrichment_status_override_reason'
      | 'patient_name'
      | 'rendering_provider_name'
      | 'start_date'
      | 'id'
    >
  > = []

  if (isSearchValueUuid === false && encountersQuery.isSuccess === true) {
    encounters = encountersQuery.data.encounters
  } else if (isSearchValueUuid === true && encounterQuery.isSuccess === true) {
    encounters = [encounterQuery.data]
  }

  const isDebouncing = debouncedQuery !== query
  const activeQuery = isSearchValueUuid ? encounterQuery : encountersQuery
  const hasMore =
    isSearchValueUuid === false &&
    encountersQuery.isSuccess === true &&
    encountersQuery.data.has_more === true

  return {
    encounters,
    hasMore,
    isLoading:
      isDebouncing ||
      activeQuery.isFetching === true ||
      activeQuery.isPlaceholderData === true,
    isSearchValueUuid,
    isSuccess: activeQuery.isSuccess === true,
  }
}
